import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { Mascot } from "@/components/Mascot";
import { Cta, TrustLine } from "@/components/public/PublicChrome";
import { Reveal } from "@/components/public/Reveal";
import { PlanPill } from "@/components/public/PageParts";
import { FAQ, JOBS, MORE } from "@/lib/publicContent";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const H2 = ({ id, children }) => <h2 id={id} className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.05]">{children}</h2>;
const Wrap = ({ label, testid, className = "", children }) => (
  <section className={`max-w-7xl mx-auto px-5 sm:px-8 mt-32 sm:mt-44 ${className}`} aria-labelledby={label} data-testid={testid}>{children}</section>
);

export function HomeHighlights() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-28 sm:mt-36" aria-labelledby="highlights-title" data-testid="home-highlights">
      <h2 id="highlights-title" className="sr-only">When BrynAI helps</h2>
      <div className="grid md:grid-cols-3 gap-12 md:gap-0 md:divide-x divide-white/[0.07]">
        {JOBS.map((j, i) => (
          <Reveal key={j.title} delay={i * 90} className="md:px-10 md:first:pl-0 md:last:pr-0" testid={`job-${j.title.split(" ")[0].toLowerCase()}`}>
            <span className="h-10 w-10 grid place-items-center rounded-xl bg-sky-400/10 text-sky-300 ring-1 ring-sky-400/20"><j.icon className="h-5 w-5" aria-hidden="true" /></span>
            <h3 className="font-display text-xl font-semibold tracking-tight mt-5">{j.title}</h3>
            <p className="text-sm sm:text-[15px] text-neutral-400 mt-2.5 leading-relaxed">{j.text}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function HomeMore() {
  return (
    <Wrap label="more-title" testid="home-more">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <H2 id="more-title">And more</H2>
        <Link to="/features" className="inline-flex items-center gap-1.5 text-sm text-sky-400 hover:text-sky-300 rounded-md" data-testid="home-features-link">
          See all features <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </Reveal>
      <Reveal delay={80} className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px overflow-hidden rounded-3xl border border-white/[0.07] bg-white/[0.07]">
        {MORE.map((f) => (
          <div key={f.name} className="bg-[#0F1320] p-6 sm:p-7" data-testid={`more-${slug(f.name)}`}>
            <f.icon className="h-5 w-5 text-sky-300" aria-hidden="true" />
            <h3 className="font-medium mt-4">{f.name}</h3>
            <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">{f.text}</p>
            {f.plan && <PlanPill testid={`more-${slug(f.name)}-plan`}>{f.plan}</PlanPill>}
          </div>
        ))}
      </Reveal>
    </Wrap>
  );
}

export function HomeFaq() {
  return (
    <Wrap label="faq-title" testid="home-faq" className="grid lg:grid-cols-12 gap-10">
      <Reveal className="lg:col-span-4"><H2 id="faq-title">Questions</H2></Reveal>
      <Reveal delay={80} className="lg:col-span-8">
        <div className="divide-y divide-white/[0.07] border-y border-white/[0.07]" data-testid="faq">
          {FAQ.map(([q, a], i) => (
            <details key={q} className="group py-6" data-testid={`faq-item-${i + 1}`}>
              <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-start justify-between gap-6 rounded-lg text-base sm:text-lg font-medium" data-testid={`faq-question-${i + 1}`}>
                {q}
                <span aria-hidden="true" className="mt-0.5 h-7 w-7 shrink-0 grid place-items-center rounded-full border border-white/10 text-sky-300 transition-transform duration-200 group-open:rotate-45"><Plus className="h-4 w-4" /></span>
              </summary>
              <p className="mt-4 max-w-2xl text-sm sm:text-[15px] text-neutral-400 leading-relaxed" data-testid={`faq-answer-${i + 1}`}>{a}</p>
            </details>
          ))}
        </div>
      </Reveal>
    </Wrap>
  );
}

export function HomeFinalCta() {
  return (
    <Wrap label="final-cta-title" testid="home-final-cta">
      <Reveal className="relative overflow-hidden rounded-[2rem] border border-white/[0.08] bg-[#0F1422] px-6 py-16 sm:px-12 sm:py-24 text-center">
        <div aria-hidden="true" className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="relative">
          <Mascot pose="happy" decorative className="h-20 w-20 sm:h-24 sm:w-24 mx-auto" />
          <h2 id="final-cta-title" className="font-display text-3xl sm:text-5xl font-semibold tracking-[-0.03em] mt-6">Start studying smarter tonight</h2>
          <div className="mt-9 flex flex-col items-center gap-4"><Cta testid="closing-signup-btn" /><TrustLine testid="closing-trust-line" /></div>
        </div>
      </Reveal>
    </Wrap>
  );
}
